// Board templates (06 "Creating a board: name, template (blank, kanban, sprint, content calendar,
// client onboarding…)"; 04 `BoardCreate.template`). A template gives a new board its statuses
// (unless the request or the space names a set), its fields and its saved views; the first view
// is the default. Views name template fields as `field:{key}`, resolved to the new field ids.
import type { FieldOptions, FieldType } from "./fields";
import { type ColumnGate, DEFAULT_STATUS_SET, type StatusDef } from "./status";
import type { Op } from "./views";

export const BOARD_TEMPLATE_KEYS = [
  "blank",
  "kanban",
  "sprint",
  "content_calendar",
  "client_onboarding",
  "ideas",
  "content_pipeline",
] as const;
export type BoardTemplateKey = (typeof BOARD_TEMPLATE_KEYS)[number];

/**
 * A template field's rule (`field_rule`): when a value must be there. `enter` holds on entering
 * one of `statuses` (creating a card there is entering it too).
 */
export type TemplateFieldRule = {
  required_on: "create" | "complete" | "both" | "enter";
  statuses?: readonly string[];
  show_on_card?: boolean;
};
export type TemplateField = {
  key: string;
  name: string;
  type: FieldType;
  options?: FieldOptions;
  rule?: TemplateFieldRule;
};
export type TemplateView = {
  kind: "board" | "list" | "table" | "calendar" | "timeline" | "gallery";
  name: string;
  group_by?: string;
  sort?: { field: string; direction: "asc" | "desc" }[];
  filter?: { all?: { field: string; op: Op; value?: unknown }[]; include_done?: boolean };
  visible_fields?: string[];
};
export type BoardTemplate = {
  statusSet: readonly StatusDef[];
  fields: readonly TemplateField[];
  /** At least one; the first is the board's default view. */
  views: readonly TemplateView[];
  /**
   * AWARENESS-MVP: the template's statuses win over the space's default set (a request's own
   * `status_set` still wins). The Ideas board's four statuses are what the skills read and write.
   */
  fixedStatuses?: boolean;
  /** BOARD-GATES: the gate presets a person picks from in Board settings (§2.5). */
  gatePresets?: readonly GatePreset[];
};

/** A B2 board-default row a preset sets (`board_gate` with no seat). */
export type PresetGateRow = {
  from_status: string | null;
  to_status: string;
  mode: "ask" | "hold" | "trust";
  hold_minutes?: number;
};

/**
 * BOARD-GATES §2.5: one preset — which columns need a person (by key, with the gate's targets)
 * and which B2 board-default rows go with it. Columns not named have no person-only gate.
 */
export type GatePreset = {
  key: "review_everything" | "approve_ideas" | "autopilot";
  name: string;
  hint: string;
  columns: Readonly<Record<string, ColumnGate>>;
  rows: readonly PresetGateRow[];
};

/** The set with the preset's person-only gates (every other column's gate removed). */
export function applyGatePreset(set: readonly StatusDef[], preset: GatePreset): StatusDef[] {
  return set.map((s) => {
    const { gate: _old, ...rest } = s;
    const g = preset.columns[s.key];
    return g ? { ...rest, gate: g } : rest;
  });
}

/** Which preset a status set matches (its gated columns and their targets), or null. */
export function matchingGatePreset(
  set: readonly StatusDef[],
  presets: readonly GatePreset[],
): GatePreset | null {
  const same = (a: ColumnGate | undefined, b: ColumnGate | undefined) =>
    JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  return presets.find((p) => set.every((s) => same(s.gate, p.columns[s.key]))) ?? null;
}

/**
 * BOARD-GATES (docs/design/2026-11-02-board-stages-gates-kpis.md §1): the content pipeline's
 * column keys and field keys, which the HTVA migration and the skills name.
 */
export const PIPELINE_STATUS = {
  research: "research",
  concepts: "concepts",
  ideas: "ideas",
  inWork: "in_work",
  review: "review",
  rework: "rework",
  approved: "approved",
  inPlan: "in_plan",
  published: "published",
  rejected: "rejected",
  parked: "parked",
} as const;

const S = PIPELINE_STATUS;
const IDEAS_GATE: ColumnGate = {
  needs_person: true,
  approve_to: S.inWork,
  rework_to: S.concepts,
  reject_to: S.rejected,
};
const REVIEW_GATE: ColumnGate = {
  needs_person: true,
  approve_to: S.approved,
  rework_to: S.rework,
  reject_to: S.rejected,
};
const HOLD_REVIEW: PresetGateRow = {
  from_status: S.review,
  to_status: S.approved,
  mode: "hold",
  hold_minutes: 1440,
};

export const CONTENT_PIPELINE_PRESETS: readonly GatePreset[] = [
  {
    key: "review_everything",
    name: "Review everything",
    hint: "You pick which ideas get made and approve every finished piece",
    columns: { [S.ideas]: IDEAS_GATE, [S.review]: REVIEW_GATE },
    rows: [],
  },
  {
    key: "approve_ideas",
    name: "Approve ideas, trust the rest",
    hint: "You pick the ideas; finished pieces go ahead unless you pull them back within a day",
    columns: { [S.ideas]: IDEAS_GATE },
    rows: [HOLD_REVIEW],
  },
  {
    key: "autopilot",
    name: "Autopilot",
    hint: "The team goes ahead; you can pull a piece back within a day, and publishing follows the outward-action policy",
    columns: {},
    rows: [HOLD_REVIEW],
  },
];

const onEnter = (...statuses: string[]): TemplateFieldRule => ({
  required_on: "enter",
  statuses,
});

/**
 * AWARENESS-MVP (docs/design/2026-11-01-companyos-awareness-and-ideas-board.md §3): the Ideas
 * board's status keys and field keys, which the platform pack and the skills name.
 */
export const IDEAS_STATUS = {
  proposed: "proposed",
  approved: "approved",
  rejected: "rejected",
  used: "used",
} as const;
export const IDEAS_FIELD = {
  why: "why",
  source: "source",
  from: "from",
  kind: "kind",
  usedIn: "used_in",
} as const;
/** The sprint's idea cap: a card title is the idea in one sentence. */
export const IDEA_TITLE_CAP = 200;

const choices = (...names: string[]) =>
  names.map((name) => ({ key: name.toLowerCase().replace(/[^a-z0-9]+/g, "_"), name }));

export const BOARD_TEMPLATES: Readonly<Record<BoardTemplateKey, BoardTemplate>> = {
  blank: {
    statusSet: DEFAULT_STATUS_SET,
    fields: [],
    views: [
      {
        kind: "list",
        name: "All tasks",
        group_by: "status",
        filter: { include_done: true },
      },
    ],
  },
  kanban: {
    statusSet: [
      { key: "backlog", name: "Backlog", done: false },
      { key: "todo", name: "To do", done: false },
      { key: "in_progress", name: "In progress", done: false, started: true },
      { key: "review", name: "Review", done: false, started: true },
      { key: "done", name: "Done", done: true },
    ],
    fields: [],
    views: [
      { kind: "board", name: "Board", group_by: "status", filter: { include_done: true } },
      { kind: "list", name: "My open work", sort: [{ field: "due_at", direction: "asc" }] },
    ],
  },
  sprint: {
    statusSet: [
      { key: "todo", name: "To do", done: false },
      { key: "in_progress", name: "In progress", done: false, started: true },
      { key: "in_review", name: "In review", done: false, started: true },
      { key: "done", name: "Done", done: true },
    ],
    fields: [
      { key: "points", name: "Points", type: "number", options: { precision: 0 } },
      {
        key: "sprint",
        name: "Sprint",
        type: "select",
        options: { choices: choices("Sprint 1", "Sprint 2", "Sprint 3") },
      },
    ],
    views: [
      { kind: "board", name: "Sprint board", group_by: "status", filter: { include_done: true } },
      {
        kind: "table",
        name: "Backlog",
        group_by: "field:sprint",
        sort: [{ field: "priority", direction: "asc" }],
        visible_fields: ["number", "title", "status", "assignee", "field:points"],
      },
    ],
  },
  content_calendar: {
    statusSet: [
      { key: "idea", name: "Idea", done: false },
      { key: "drafting", name: "Drafting", done: false, started: true },
      { key: "review", name: "Review", done: false, started: true },
      { key: "scheduled", name: "Scheduled", done: false, started: true },
      { key: "published", name: "Published", done: true },
    ],
    fields: [
      {
        key: "channel",
        name: "Channel",
        type: "select",
        options: { choices: choices("Blog", "Social", "Email", "Video") },
      },
      { key: "publish_on", name: "Publish on", type: "date" },
    ],
    views: [
      {
        kind: "calendar",
        name: "Calendar",
        sort: [{ field: "field:publish_on", direction: "asc" }],
        filter: { include_done: true },
      },
      { kind: "board", name: "Pipeline", group_by: "status", filter: { include_done: true } },
    ],
  },
  client_onboarding: {
    statusSet: [
      { key: "not_started", name: "Not started", done: false },
      { key: "in_progress", name: "In progress", done: false, started: true },
      { key: "waiting_on_client", name: "Waiting on client", done: false },
      { key: "done", name: "Done", done: true },
    ],
    fields: [
      { key: "client", name: "Client", type: "text" },
      { key: "go_live", name: "Go-live", type: "date" },
    ],
    views: [
      {
        kind: "list",
        name: "Onboarding",
        group_by: "status",
        sort: [{ field: "due_at", direction: "asc" }],
        filter: { include_done: true },
      },
      { kind: "timeline", name: "Timeline", sort: [{ field: "start_at", direction: "asc" }] },
    ],
  },
  // AWARENESS-MVP: one Ideas board per content stream, shared by that stream's DEs. DEs file
  // into Proposed; a person approves or rejects (Rejected is kept so the idea is not refiled);
  // the sprint takes an Approved card and moves it to Used with `Used in`.
  ideas: {
    fixedStatuses: true,
    statusSet: [
      { key: IDEAS_STATUS.proposed, name: "Proposed", done: false },
      { key: IDEAS_STATUS.approved, name: "Approved", done: false, started: true },
      { key: IDEAS_STATUS.rejected, name: "Rejected", done: true },
      { key: IDEAS_STATUS.used, name: "Used", done: true },
    ],
    fields: [
      { key: IDEAS_FIELD.why, name: "Why", type: "text" },
      { key: IDEAS_FIELD.source, name: "Source", type: "text" },
      { key: IDEAS_FIELD.from, name: "From", type: "text" },
      {
        key: IDEAS_FIELD.kind,
        name: "Kind",
        type: "select",
        options: { choices: choices("List", "Argument", "Story", "Other") },
      },
      { key: IDEAS_FIELD.usedIn, name: "Used in", type: "text" },
    ],
    views: [
      { kind: "board", name: "Ideas", group_by: "status", filter: { include_done: true } },
      {
        kind: "table",
        name: "All ideas",
        sort: [{ field: "created_at", direction: "asc" }],
        filter: { include_done: true },
        visible_fields: [
          "title",
          "status",
          "field:why",
          "field:kind",
          "field:from",
          "field:source",
          "field:used_in",
        ],
      },
    ],
  },
  // BOARD-GATES (§1): Research → Concepts → Ideas → In work → Review → Approved → In plan →
  // Published, with Rework, Rejected and Parked beside. The default is "Review everything": a
  // person holds Ideas and Review (the HTVA preset); the presets switch it per client.
  content_pipeline: {
    fixedStatuses: true,
    gatePresets: CONTENT_PIPELINE_PRESETS,
    statusSet: [
      { key: S.research, name: "Research", done: false },
      { key: S.concepts, name: "Concepts", done: false },
      { key: S.ideas, name: "Ideas", done: false, gate: IDEAS_GATE },
      { key: S.inWork, name: "In work", done: false, started: true },
      { key: S.review, name: "Review", done: false, started: true, gate: REVIEW_GATE },
      { key: S.rework, name: "Rework", done: false, started: true },
      { key: S.approved, name: "Approved", done: false, started: true },
      { key: S.inPlan, name: "In plan", done: false, started: true },
      { key: S.published, name: "Published", done: true },
      { key: S.rejected, name: "Rejected", done: true },
      { key: S.parked, name: "Parked", done: false },
    ],
    fields: [
      // Research (§5): the finding's signal and evidence
      {
        key: "signal",
        name: "Signal",
        type: "select",
        options: { choices: choices("Urgent", "Trending", "Rising", "Evergreen") },
        rule: { ...onEnter(S.research), show_on_card: true },
      },
      { key: "why_it_matters", name: "Why it matters", type: "text", rule: onEnter(S.research) },
      // BOARD-RESULTS (research signals): one line linking the finding to the offer; optional.
      { key: "offer_fit", name: "Offer fit", type: "text" },
      { key: "sources", name: "Sources", type: "number", rule: onEnter(S.research) },
      { key: "evidence", name: "Evidence", type: "text", rule: onEnter(S.research) },
      {
        key: "trend_direction",
        name: "Trend direction",
        type: "select",
        options: { choices: choices("Up", "Flat", "Down") },
      },
      { key: "search_interest", name: "Search interest", type: "number" },
      { key: "engagement", name: "Engagement", type: "number" },
      { key: "expires", name: "Expires", type: "date" },
      // Concepts: the pain point it serves and the angle sketch
      { key: "pain_point", name: "Pain point", type: "text", rule: onEnter(S.concepts) },
      { key: "angle_sketch", name: "Angle sketch", type: "text", rule: onEnter(S.concepts) },
      // Ideas: the well-formed idea
      { key: "reason", name: "Reason", type: "text", rule: onEnter(S.ideas) },
      { key: "angle", name: "Angle", type: "text", rule: onEnter(S.ideas) },
      { key: "hook", name: "Hook", type: "text", rule: onEnter(S.ideas) },
      { key: "cta", name: "CTA", type: "text", rule: onEnter(S.ideas) },
      {
        key: "format",
        name: "Format",
        type: "select",
        options: { choices: choices("Carousel", "Reel", "Post", "Story", "Article", "Email") },
        rule: { ...onEnter(S.ideas), show_on_card: true },
      },
      {
        key: "channel",
        name: "Channel",
        type: "select",
        options: {
          choices: choices(
            "Instagram",
            "LinkedIn",
            "Facebook",
            "TikTok",
            "YouTube",
            "Blog",
            "Email",
          ),
        },
        rule: { ...onEnter(S.ideas, S.inPlan), show_on_card: true },
      },
      { key: "precedent", name: "Precedent", type: "text", rule: onEnter(S.ideas) },
      // In work, Review: who makes it, and what a person reviews
      { key: "owner_helper", name: "Owner helper", type: "text", rule: onEnter(S.inWork) },
      { key: "asset", name: "Asset or draft", type: "url", rule: onEnter(S.review) },
      { key: "self_review", name: "Self-review", type: "text", rule: onEnter(S.review) },
      // In plan, Published, Parked
      { key: "publish_at", name: "Publish at", type: "date", rule: onEnter(S.inPlan) },
      { key: "post_link", name: "Post link", type: "url", rule: onEnter(S.published) },
      { key: "waiting_on", name: "Waiting on", type: "text", rule: onEnter(S.parked) },
    ],
    views: [
      { kind: "board", name: "Pipeline", group_by: "status", filter: { include_done: true } },
      {
        kind: "table",
        name: "Research",
        filter: { all: [{ field: "status", op: "eq", value: S.research }] },
        sort: [{ field: "field:signal", direction: "asc" }],
        visible_fields: [
          "title",
          "field:signal",
          "field:why_it_matters",
          "field:sources",
          "field:engagement",
          "field:search_interest",
          "field:expires",
        ],
      },
      {
        kind: "calendar",
        name: "Calendar",
        sort: [{ field: "field:publish_at", direction: "asc" }],
        filter: { include_done: true },
      },
    ],
  },
};

/** Replaces `field:{key}` refs of a template view with `field:{id}` of the created fields. */
export function resolveTemplateRef(ref: string, ids: ReadonlyMap<string, string>): string {
  const m = /^field:([a-z_]+)$/.exec(ref);
  if (!m) return ref;
  const id = ids.get(m[1] as string);
  if (!id) throw new Error(`template view names an unknown field ${ref}`);
  return `field:${id}`;
}

export function resolveTemplateView(view: TemplateView, ids: ReadonlyMap<string, string>) {
  const r = (ref: string) => resolveTemplateRef(ref, ids);
  return {
    kind: view.kind,
    name: view.name,
    group_by: view.group_by ? r(view.group_by) : null,
    sort: (view.sort ?? []).map((s) => ({ ...s, field: r(s.field) })),
    filter: {
      ...(view.filter?.all
        ? { all: view.filter.all.map((c) => ({ ...c, field: r(c.field) })) }
        : {}),
      ...(view.filter?.include_done !== undefined
        ? { include_done: view.filter.include_done }
        : {}),
    },
    visible_fields: (view.visible_fields ?? []).map(r),
  };
}
